// Constants for the native (StoreKit via RevenueCat) purchase path.
//
// Nothing here is imported by the web build's hot paths: this module is plain
// data, has no dependency beyond @/lib/platform, and the RevenueCat SDK itself
// is only ever reached through a dynamic import inside an isNative() guard.

/**
 * The entitlement identifier configured in the RevenueCat dashboard.
 * The server webhook reads the same value from REVENUECAT_ENTITLEMENT_ID and
 * defaults to the same string, so the two cannot drift silently.
 */
export const ENTITLEMENT_ID = 'premium';

/**
 * The App Store product. One product, one price, billed from day one, exactly
 * like the single Stripe payment link in src/lib/stripeLinks.js.
 *
 * The Apple PRICE is deliberately not in this file. Stripe charges
 * EUR 11.49 / USD 13.49 / GBP 9.99 and Apple's price tiers will not land on
 * those numbers, and Apple's commission is charged on the price net of VAT.
 * Picking the tier is a founder decision, and the price the user sees must come
 * from StoreKit itself (offering.product.priceString) so the number on screen is
 * always the number Apple will actually charge, in the user's own currency.
 */
export const PRODUCT_ID = 'com.timohelps.app.premium.monthly';

/**
 * Public RevenueCat SDK key for iOS (appl_...). Public by design: it can only
 * read and start purchases for this app, never issue entitlements. It still
 * comes from the environment rather than a literal, so a key rotation is a
 * deploy and not a code change, and so a staging project can differ.
 */
export const REVENUECAT_IOS_API_KEY =
  (import.meta.env && import.meta.env.VITE_REVENUECAT_IOS_API_KEY) || '';

/**
 * Apple's documented deep link to Settings > Apple Account > Subscriptions.
 *
 * Note we do NOT use CustomerInfo.managementURL when it is an https apps.apple.com
 * URL: that would be a normal in-webview navigation and would replace the app's
 * own UI with a web page. A non-http scheme is handed to the system by the
 * native bridge, which is the behaviour we want here.
 */
export const APPLE_MANAGE_SUBSCRIPTIONS_URL = 'itms-apps://apps.apple.com/account/subscriptions';

/**
 * localStorage key for the on-device entitlement cache.
 *
 * Deliberately separate from `ttt_has_plan`. That flag is the existing, load
 * bearing web fallback for the case where the Supabase read comes back empty
 * for a real payer, and AuthContext owns its lifecycle. Writing StoreKit state
 * into it would entangle two different failure stories.
 */
export const ENTITLEMENT_CACHE_KEY = 'ttt_native_entitlement';

/**
 * Grace applied ONLY to the offline cache path, when RevenueCat cannot be
 * reached at all. Same three days AuthContext.jsx:92 already allows a Stripe
 * row, for the same reason: a false positive costs one month, a false negative
 * locks a paying customer out of the app they just bought.
 */
export const CACHE_GRACE_MS = 3 * 24 * 60 * 60 * 1000;
